import React, { useState, useEffect } from 'react';
import Sidebar from '../components/Sidebar';
import Header from '../components/Header';
import { mcpAPI } from '../services/api';

const MCP = () => {
  const [tools, setTools] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [editingTool, setEditingTool] = useState(null);
  const [deletingTool, setDeletingTool] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [testingTool, setTestingTool] = useState(null);
  const [showTestModal, setShowTestModal] = useState(false);
  const [testParams, setTestParams] = useState('');
  const [testResult, setTestResult] = useState(null);
  const [isTesting, setIsTesting] = useState(false);
  const [editingTokenId, setEditingTokenId] = useState(null);
  const [tokenInputs, setTokenInputs] = useState({});
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    endpoint: '',
    method: 'POST',
    headers: '',
    parameters_schema: '',
    is_active: true,
    category: 'general',
    timeout: 30000,
    retry_count: 3
  });

  const categories = ['general', 'data', 'communication', 'automation', 'integration'];
  const methods = ['GET', 'POST', 'PUT', 'DELETE', 'PATCH'];

  useEffect(() => {
    fetchTools();
  }, []);

  const fetchTools = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await mcpAPI.getAll();
      if (response.data.success) {
        setTools(response.data.data || []);
      }
    } catch (err) {
      console.error('Failed to fetch MCP tools:', err);
      setError('Failed to load MCP tools');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  const handleEdit = (tool) => {
    setEditingTool(tool);
    setFormData({
      name: tool.name || '',
      description: tool.description || '',
      endpoint: tool.endpoint || '',
      method: tool.method || 'POST',
      headers: tool.headers ? JSON.stringify(tool.headers, null, 2) : '',
      parameters_schema: tool.parameters_schema ? JSON.stringify(tool.parameters_schema, null, 2) : '',
      is_active: tool.is_active !== false,
      category: tool.category || 'general',
      timeout: tool.timeout || 30000,
      retry_count: tool.retry_count || 3
    });
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingTool(null);
    setFormData({
      name: '',
      description: '',
      endpoint: '',
      method: 'POST',
      headers: '',
      parameters_schema: '',
      is_active: true,
      category: 'general',
      timeout: 30000,
      retry_count: 3
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');

    try {
      // Parse JSON fields
      let headers = {};
      let parameters_schema = {};

      if (formData.headers) {
        try {
          headers = JSON.parse(formData.headers);
        } catch (err) {
          setError('Invalid JSON format for headers');
          setIsSubmitting(false);
          return;
        }
      }

      if (formData.parameters_schema) {
        try {
          parameters_schema = JSON.parse(formData.parameters_schema);
        } catch (err) {
          setError('Invalid JSON format for parameters schema');
          setIsSubmitting(false);
          return;
        }
      }

      const payload = {
        name: formData.name,
        description: formData.description,
        endpoint: formData.endpoint,
        method: formData.method,
        headers,
        parameters_schema,
        is_active: formData.is_active,
        category: formData.category,
        timeout: parseInt(formData.timeout),
        retry_count: parseInt(formData.retry_count)
      };

      let response;
      if (editingTool) {
        response = await mcpAPI.update(editingTool.id, payload);
      } else {
        response = await mcpAPI.create(payload);
      }

      if (response.data.success) {
        handleCloseModal();
        fetchTools();
      }
    } catch (err) {
      console.error('Failed to save MCP tool:', err);
      setError(err.response?.data?.error?.message || 'Failed to save MCP tool');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleToggle = async (toolId) => {
    try {
      await mcpAPI.toggle(toolId);
      fetchTools();
    } catch (err) {
      console.error('Failed to toggle MCP tool:', err);
      alert('Failed to toggle tool status');
    }
  };

  const handleDeleteClick = (tool) => {
    setDeletingTool(tool);
    setShowDeleteConfirm(true);
  };

  const handleDeleteConfirm = async () => {
    if (!deletingTool) return;

    try {
      await mcpAPI.delete(deletingTool.id);
      setShowDeleteConfirm(false);
      setDeletingTool(null);
      fetchTools();
    } catch (err) {
      console.error('Failed to delete MCP tool:', err);
      alert('Failed to delete tool: ' + (err.response?.data?.error?.message || 'Unknown error'));
    }
  };

  const handleDeleteCancel = () => {
    setShowDeleteConfirm(false);
    setDeletingTool(null);
  };

  const handleTestClick = (tool) => {
    setTestingTool(tool);
    setShowTestModal(true);
    setTestParams('');
    setTestResult(null);

    // Set default test parameters based on schema
    if (tool.parameters_schema && Object.keys(tool.parameters_schema).length > 0) {
      try {
        const schema = tool.parameters_schema;
        const defaultParams = {};

        // Special handling for Web Search - convert to URL
        const isWebSearch = tool.name.includes('Web Search');

        if (schema.properties) {
          Object.keys(schema.properties).forEach(key => {
            const prop = schema.properties[key];

            // Auto-fill token if available
            if (key === 'token' && tool.api_token) {
              defaultParams[key] = tool.api_token;
            } else if (prop.default !== undefined) {
              // Use schema default value if available
              defaultParams[key] = prop.default;
            } else if (prop.type === 'string') {
              // Use appropriate placeholder based on field name
              if (key === 'url') {
                if (isWebSearch) {
                  // For Web Search, skip URL as we'll use query to generate it
                  return;
                }
                defaultParams[key] = 'https://geo.brdtest.com/mygeo.json';
              } else if (key === 'zone') {
                defaultParams[key] = 'web_unlocker1';
              } else if (key === 'format') {
                defaultParams[key] = 'raw';
              } else if (key === 'query') {
                defaultParams[key] = 'latest AI news';
              } else if (key === 'country') {
                defaultParams[key] = 'us';
              } else {
                defaultParams[key] = '';
              }
            } else if (prop.type === 'number') {
              defaultParams[key] = prop.default || 0;
            } else if (prop.type === 'boolean') {
              defaultParams[key] = prop.default || false;
            } else if (prop.type === 'object') {
              defaultParams[key] = {};
            } else if (prop.type === 'array') {
              defaultParams[key] = [];
            }
          });
        }

        setTestParams(JSON.stringify(defaultParams, null, 2));
      } catch (err) {
        console.error('Failed to generate default params:', err);
        setTestParams('{}');
      }
    } else {
      setTestParams('{}');
    }
  };

  const handleTestSubmit = async () => {
    if (!testingTool) return;

    setIsTesting(true);
    setTestResult(null);

    try {
      // Parse test parameters
      let params = {};
      if (testParams) {
        try {
          params = JSON.parse(testParams);
        } catch (err) {
          setTestResult({
            success: false,
            error: 'Invalid JSON format for test parameters',
            timestamp: new Date().toISOString()
          });
          setIsTesting(false);
          return;
        }
      }

      // Use backend proxy to avoid CORS issues
      const response = await mcpAPI.test(testingTool.id, params);

      if (response.data.success) {
        setTestResult({
          success: true,
          status: response.data.data.status,
          statusText: response.data.data.statusText,
          responseTime: response.data.data.responseTime,
          data: response.data.data.data,
          timestamp: response.data.data.timestamp
        });
      } else {
        setTestResult({
          success: false,
          status: response.data.data?.status,
          statusText: response.data.data?.statusText,
          responseTime: response.data.data?.responseTime,
          error: response.data.data?.error || 'Test failed',
          data: response.data.data?.data,
          timestamp: response.data.data?.timestamp || new Date().toISOString()
        });
      }
    } catch (err) {
      console.error('Test error:', err);
      setTestResult({
        success: false,
        error: err.response?.data?.error?.message || err.message || 'Unknown error',
        timestamp: new Date().toISOString()
      });
    } finally {
      setIsTesting(false);
    }
  };

  const handleTestClose = () => {
    setShowTestModal(false);
    setTestingTool(null);
    setTestParams('');
    setTestResult(null);
  };

  const handleTokenEdit = (toolId, currentToken) => {
    setEditingTokenId(toolId);
    setTokenInputs({ ...tokenInputs, [toolId]: currentToken || '' });
  };

  const handleTokenCancel = (toolId) => {
    setEditingTokenId(null);
    const updatedInputs = { ...tokenInputs };
    delete updatedInputs[toolId];
    setTokenInputs(updatedInputs);
  };

  const handleTokenSave = async (toolId) => {
    try {
      const token = tokenInputs[toolId] || '';
      await mcpAPI.updateToken(toolId, token);
      setEditingTokenId(null);
      fetchTools(); // Refresh to show updated token
    } catch (err) {
      console.error('Failed to update API token:', err);
      alert('Failed to update API token');
    }
  };

  const handleTokenChange = (toolId, value) => {
    setTokenInputs({ ...tokenInputs, [toolId]: value });
  };

  const getCategoryColor = (category) => {
    const colors = {
      general: 'bg-gray-100 text-gray-800',
      data: 'bg-blue-100 text-blue-800',
      communication: 'bg-green-100 text-green-800',
      automation: 'bg-purple-100 text-purple-800',
      integration: 'bg-orange-100 text-orange-800'
    };
    return colors[category] || colors.general;
  };

  const getMethodColor = (method) => {
    const colors = {
      GET: 'bg-blue-50 text-blue-700 border-blue-200',
      POST: 'bg-green-50 text-green-700 border-green-200',
      PUT: 'bg-orange-50 text-orange-700 border-orange-200',
      DELETE: 'bg-red-50 text-red-700 border-red-200',
      PATCH: 'bg-purple-50 text-purple-700 border-purple-200'
    };
    return colors[method] || colors.GET;
  };

  if (loading) {
    return (
      <div className="flex h-screen bg-gray-50">
        <Sidebar />
        <div className="flex-1 flex flex-col overflow-hidden">
          <Header title="MCP Tools" />
          <div className="flex-1 flex items-center justify-center">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
              <p className="mt-4 text-gray-600">Loading MCP tools...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen bg-gray-50">
      <Sidebar />
      <div className="flex-1 flex flex-col overflow-hidden">
        <Header title="MCP Tools" />
        <div className="flex-1 overflow-y-auto p-6">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4">
              {error}
            </div>
          )}

          {/* Top Actions */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-xl font-bold text-gray-900">Model Context Protocol Tools</h2>
                <p className="text-sm text-gray-600 mt-1">
                  Manage external tools and integrations for AI models
                </p>
              </div>
              <button
                onClick={() => setShowModal(true)}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition"
              >
                + Add MCP Tool
              </button>
            </div>
          </div>

          {tools.length === 0 ? (
            <div className="text-center py-12 bg-white rounded-lg border border-gray-200">
              <svg className="mx-auto h-16 w-16 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
              <h3 className="mt-4 text-lg font-medium text-gray-900">No MCP Tools</h3>
              <p className="mt-2 text-sm text-gray-500">
                Get started by adding your first MCP tool integration
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {tools.map((tool) => (
                <div
                  key={tool.id}
                  className="bg-white rounded-lg shadow-sm border-2 border-gray-200 p-6 hover:shadow-md transition-shadow"
                >
                  {/* Header */}
                  <div className="flex items-center justify-between mb-3">
                    <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getCategoryColor(tool.category)}`}>
                      {tool.category}
                    </span>
                    <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                      tool.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                    }`}>
                      {tool.is_active ? '● Active' : '○ Inactive'}
                    </span>
                  </div>

                  {/* Tool Name */}
                  <h3 className="text-xl font-bold text-gray-900 mb-2">{tool.name}</h3>

                  {/* Description */}
                  <p className="text-sm text-gray-600 mb-4 line-clamp-2">
                    {tool.description || 'No description'}
                  </p>

                  {/* Endpoint */}
                  <div className="bg-gray-50 rounded-lg p-3 mb-4">
                    <div className="flex items-center gap-2 mb-1">
                      <span className={`px-2 py-0.5 rounded text-xs font-bold border ${getMethodColor(tool.method)}`}>
                        {tool.method}
                      </span>
                    </div>
                    <p className="text-xs text-gray-600 font-mono break-all">{tool.endpoint}</p>
                  </div>

                  {/* Stats */}
                  <div className="space-y-2 mb-4">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Timeout</span>
                      <span className="font-semibold">{tool.timeout}ms</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Retry Count</span>
                      <span className="font-semibold">{tool.retry_count}</span>
                    </div>
                  </div>

                  {/* API Token Section */}
                  <div className="mb-4 border-t border-gray-200 pt-4">
                    <div className="flex items-center justify-between mb-2">
                      <label className="text-xs font-semibold text-gray-700">API Token</label>
                      {tool.api_token && editingTokenId !== tool.id && (
                        <span className="text-xs text-green-600 flex items-center gap-1">
                          <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                          </svg>
                          Configured
                        </span>
                      )}
                    </div>
                    {editingTokenId === tool.id ? (
                      <div className="space-y-2">
                        <input
                          type="password"
                          value={tokenInputs[tool.id] || ''}
                          onChange={(e) => handleTokenChange(tool.id, e.target.value)}
                          placeholder="Enter API token"
                          className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent font-mono"
                        />
                        <div className="flex gap-2">
                          <button
                            onClick={() => handleTokenSave(tool.id)}
                            className="flex-1 py-1.5 bg-blue-600 text-white rounded text-xs font-medium hover:bg-blue-700"
                          >
                            Save
                          </button>
                          <button
                            onClick={() => handleTokenCancel(tool.id)}
                            className="flex-1 py-1.5 border border-gray-300 rounded text-xs font-medium hover:bg-gray-50"
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2">
                        <input
                          type="password"
                          value={tool.api_token || ''}
                          readOnly
                          placeholder="Not configured"
                          className="flex-1 px-2 py-1.5 text-xs border border-gray-300 rounded bg-gray-50 font-mono"
                        />
                        <button
                          onClick={() => handleTokenEdit(tool.id, tool.api_token)}
                          className="px-3 py-1.5 text-xs font-medium text-blue-600 hover:bg-blue-50 rounded"
                        >
                          {tool.api_token ? 'Edit' : 'Add'}
                        </button>
                      </div>
                    )}
                  </div>

                  {/* Action Buttons */}
                  <div className="grid grid-cols-3 gap-2 mb-2">
                    <button
                      onClick={() => handleEdit(tool)}
                      className="py-2 border-2 border-gray-300 rounded-lg text-sm font-medium hover:bg-gray-50 transition"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleTestClick(tool)}
                      className="py-2 bg-blue-100 text-blue-800 rounded-lg text-sm font-medium hover:bg-blue-200 transition"
                    >
                      Test
                    </button>
                    <button
                      onClick={() => handleToggle(tool.id)}
                      className={`py-2 rounded-lg text-sm font-medium transition ${
                        tool.is_active
                          ? 'bg-yellow-100 text-yellow-800 hover:bg-yellow-200'
                          : 'bg-green-100 text-green-800 hover:bg-green-200'
                      }`}
                    >
                      {tool.is_active ? 'Disable' : 'Enable'}
                    </button>
                  </div>

                  <button
                    onClick={() => handleDeleteClick(tool)}
                    className="w-full py-2 bg-red-100 text-red-800 rounded-lg text-sm font-medium hover:bg-red-200 transition"
                  >
                    Delete
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-2xl font-bold text-gray-900">
                {editingTool ? 'Edit MCP Tool' : 'Add MCP Tool'}
              </h2>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Tool Name *
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="e.g., Weather API"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Category *
                  </label>
                  <select
                    name="category"
                    value={formData.category}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    required
                  >
                    {categories.map(cat => (
                      <option key={cat} value={cat}>{cat.charAt(0).toUpperCase() + cat.slice(1)}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Description
                </label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  rows="3"
                  placeholder="Describe what this tool does"
                />
              </div>

              <div className="grid grid-cols-4 gap-4">
                <div className="col-span-1">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Method *
                  </label>
                  <select
                    name="method"
                    value={formData.method}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    required
                  >
                    {methods.map(method => (
                      <option key={method} value={method}>{method}</option>
                    ))}
                  </select>
                </div>

                <div className="col-span-3">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Endpoint URL *
                  </label>
                  <input
                    type="url"
                    name="endpoint"
                    value={formData.endpoint}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="https://api.example.com/endpoint"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Headers (JSON)
                </label>
                <textarea
                  name="headers"
                  value={formData.headers}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent font-mono text-sm"
                  rows="4"
                  placeholder='{"Authorization": "Bearer token", "Content-Type": "application/json"}'
                />
                <p className="mt-1 text-xs text-gray-500">Optional request headers in JSON format</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Parameters Schema (JSON)
                </label>
                <textarea
                  name="parameters_schema"
                  value={formData.parameters_schema}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent font-mono text-sm"
                  rows="6"
                  placeholder='{"type": "object", "properties": {"query": {"type": "string"}}, "required": ["query"]}'
                />
                <p className="mt-1 text-xs text-gray-500">JSON Schema for tool parameters</p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Timeout (ms)
                  </label>
                  <input
                    type="number"
                    name="timeout"
                    value={formData.timeout}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    min="1000"
                    max="120000"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Retry Count
                  </label>
                  <input
                    type="number"
                    name="retry_count"
                    value={formData.retry_count}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    min="0"
                    max="10"
                  />
                </div>
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  name="is_active"
                  checked={formData.is_active}
                  onChange={handleInputChange}
                  className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                />
                <label className="ml-2 text-sm font-medium text-gray-700">
                  Active (enable this tool immediately)
                </label>
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={handleCloseModal}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                  disabled={isSubmitting}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                >
                  {isSubmitting
                    ? (editingTool ? 'Updating...' : 'Creating...')
                    : (editingTool ? 'Update Tool' : 'Create Tool')
                  }
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && deletingTool && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <h2 className="text-xl font-bold text-gray-900 mb-4">Confirm Delete</h2>
            <p className="text-gray-700 mb-2">
              Are you sure you want to delete this MCP tool?
            </p>
            <div className="bg-gray-50 p-3 rounded-lg mb-6">
              <p className="font-semibold text-gray-900">{deletingTool.name}</p>
              <p className="text-sm text-gray-600">{deletingTool.endpoint}</p>
            </div>
            <p className="text-sm text-red-600 font-semibold mb-6">
              ⚠️ Warning: This action cannot be undone.
            </p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={handleDeleteCancel}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteConfirm}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
              >
                Delete Tool
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Test Modal */}
      {showTestModal && testingTool && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-2xl font-bold text-gray-900">Test MCP Tool</h2>
              <p className="text-sm text-gray-600 mt-1">{testingTool.name}</p>
            </div>

            <div className="p-6 space-y-6">
              {/* Tool Info */}
              <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                <div className="flex items-center gap-2">
                  <span className={`px-2 py-0.5 rounded text-xs font-bold border ${getMethodColor(testingTool.method)}`}>
                    {testingTool.method}
                  </span>
                  <span className="text-sm font-mono text-gray-700">{testingTool.endpoint}</span>
                </div>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-gray-600">Timeout:</span>
                    <span className="ml-2 font-semibold">{testingTool.timeout}ms</span>
                  </div>
                  <div>
                    <span className="text-gray-600">Category:</span>
                    <span className={`ml-2 px-2 py-0.5 rounded text-xs font-semibold ${getCategoryColor(testingTool.category)}`}>
                      {testingTool.category}
                    </span>
                  </div>
                </div>
              </div>

              {/* Test Parameters */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Test Parameters (JSON)
                </label>
                <textarea
                  value={testParams}
                  onChange={(e) => setTestParams(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent font-mono text-sm"
                  rows="10"
                  placeholder='{"key": "value"}'
                />
                <p className="mt-1 text-xs text-gray-500">
                  Enter test parameters in JSON format according to the tool's schema
                </p>

                {/* Bright Data specific help */}
                {testingTool.name.includes('Bright Data') && (
                  <div className="mt-3 bg-blue-50 border border-blue-200 rounded-lg p-3">
                    <div className="flex items-start gap-2">
                      <svg className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                      </svg>
                      <div className="text-xs text-blue-800">
                        <p className="font-semibold mb-1">Bright Data API Guide:</p>
                        <ul className="list-disc list-inside space-y-1">
                          <li><strong>token</strong>: Your Bright Data API token (get it from <a href="https://brightdata.com" target="_blank" rel="noopener noreferrer" className="underline">brightdata.com</a>)</li>
                          {testingTool.endpoint.includes('api.brightdata.com/request') ? (
                            <>
                              <li>Token will be sent in <code className="bg-blue-100 px-1 rounded">Authorization: Bearer</code> header</li>
                              <li>Other parameters will be sent in request body</li>
                              <li>Example for Web Unlocker:
                                <pre className="bg-blue-100 p-2 rounded mt-1 text-xs font-mono overflow-x-auto">{`{
  "token": "your_api_token",
  "zone": "web_unlocker1",
  "url": "https://example.com",
  "format": "raw"
}`}</pre>
                              </li>
                            </>
                          ) : (
                            <>
                              <li>The tool will automatically format the request as JSON-RPC 2.0</li>
                              <li>Token will be sent in Authorization header as Bearer token</li>
                              <li>Other parameters will be sent in the JSON-RPC params.arguments field</li>
                            </>
                          )}
                          {testingTool.name.includes('Browser Control') || testingTool.name.includes('Amazon') || testingTool.name.includes('Google Maps') ? (
                            <li><strong>pro</strong>: Set to "1" to enable pro mode (required for this tool)</li>
                          ) : null}
                          <li>Free tier includes 5,000 requests with Web Search and Web Unlocker</li>
                          <li>Common errors:
                            <ul className="list-disc list-inside ml-4 mt-1">
                              <li>-32000: Missing/invalid token or required parameters</li>
                              <li>401: Invalid API token</li>
                              <li>429: Rate limit exceeded</li>
                            </ul>
                          </li>
                        </ul>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Test Button */}
              <div className="flex justify-center">
                <button
                  onClick={handleTestSubmit}
                  disabled={isTesting}
                  className="px-6 py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isTesting ? (
                    <span className="flex items-center gap-2">
                      <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                      </svg>
                      Testing...
                    </span>
                  ) : (
                    'Run Test'
                  )}
                </button>
              </div>

              {/* Test Results */}
              {testResult && (
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-gray-900">Test Results</h3>

                  {/* Status */}
                  <div className={`p-4 rounded-lg ${testResult.success ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
                    <div className="flex items-center gap-2 mb-2">
                      {testResult.success ? (
                        <>
                          <svg className="w-5 h-5 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                          </svg>
                          <span className="font-semibold text-green-800">Success</span>
                        </>
                      ) : (
                        <>
                          <svg className="w-5 h-5 text-red-600" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                          </svg>
                          <span className="font-semibold text-red-800">Failed</span>
                        </>
                      )}
                    </div>

                    {testResult.status && (
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <span className={testResult.success ? 'text-green-700' : 'text-red-700'}>Status:</span>
                          <span className="ml-2 font-semibold">{testResult.status} {testResult.statusText}</span>
                        </div>
                        <div>
                          <span className={testResult.success ? 'text-green-700' : 'text-red-700'}>Response Time:</span>
                          <span className="ml-2 font-semibold">{testResult.responseTime}ms</span>
                        </div>
                      </div>
                    )}

                    {testResult.error && (
                      <div className="mt-2">
                        <span className="text-red-700 font-semibold">Error:</span>
                        <p className="text-red-800 font-mono text-sm mt-1">{testResult.error}</p>
                      </div>
                    )}

                    <div className="mt-2 text-xs text-gray-600">
                      {new Date(testResult.timestamp).toLocaleString()}
                    </div>
                  </div>

                  {/* Response Data */}
                  {testResult.data && (
                    <div>
                      <h4 className="text-sm font-semibold text-gray-700 mb-2">Response Data</h4>
                      <div className="bg-gray-900 text-green-400 rounded-lg p-4 overflow-x-auto">
                        <pre className="text-xs font-mono whitespace-pre-wrap">
                          {typeof testResult.data === 'string'
                            ? testResult.data
                            : JSON.stringify(testResult.data, null, 2)
                          }
                        </pre>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="p-6 border-t border-gray-200 flex justify-end">
              <button
                onClick={handleTestClose}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MCP;
